import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';

import { CountryCode } from 'common/api-types/common';
import { UseQueryConfig } from 'common/query/types';

import {
  fetchSingleOperation,
  fetchBulkOperations,
  fetchOverrideDraftSummary,
  fetchOverridesList,
} from './api';
import { BulkOperation, BulkOperationList, OverridesDraft } from './api-types';
import { isFinalStatus } from './OverridesView/utils';
import { FetchOverridesListParams } from './types';

export const OVERRIDE_MANAGEMENT_KEY_ROOT = 'override-management';
export const BULK_OPERATIONS_QUERY_KEY_ROOT = 'bulkOperations';
const BULK_OPERATION_STATUS_QUERY_KEY_ROOT = 'override-bulk-operation-status';

export function useOverrideDraftSummaryQuery(
  draftId: OverridesDraft['id'],
  countryCode: CountryCode,
  enabled: boolean,
) {
  return useQuery({
    queryFn: () => fetchOverrideDraftSummary(draftId, countryCode),
    queryKey: [OVERRIDE_MANAGEMENT_KEY_ROOT, countryCode, draftId],
    enabled,
  });
}

export function useListOverridesQuery(params: FetchOverridesListParams, enabled: boolean) {
  return useQuery({
    queryFn: () => fetchOverridesList({ ...params, pageSize: 100 }),
    queryKey: [OVERRIDE_MANAGEMENT_KEY_ROOT, params],
    enabled,
  });
}

export function useBulkOperationStateQuery(
  operationId: string,
  options: UseQueryConfig<BulkOperation, AxiosError> & {
    /**
     * Triggers when the query results successfully, and the operation status changes.
     */
    onStatusChange?: (data: BulkOperation) => void;
    /**
     * Triggers when the query responds with an error.
     */
    onError?: (error: Error) => void;
  } = {},
) {
  const { onStatusChange, onError, ...rest } = options;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const previousStatusRef = useRef<BulkOperation['status'] | undefined>(undefined);
  const previousOperationIdRef = useRef(operationId);

  const result = useQuery({
    enabled: options?.enabled ?? !!operationId,
    queryKey: [BULK_OPERATION_STATUS_QUERY_KEY_ROOT, operationId],
    queryFn: () => fetchSingleOperation(operationId),
    refetchInterval: (query) => {
      const data = query.state.data;

      if (data) {
        const inProgress = !isFinalStatus(data.status);

        if (inProgress) {
          return 5000;
        }
      }

      return false;
    },
    retry: false,
    // Disabling all caching so that the polling doesn't return old data
    gcTime: 0,
    staleTime: 0,
    ...rest,
  });

  /**
   * Detects status changes and calls onStatusChange.
   */
  useEffect(() => {
    if (previousOperationIdRef.current !== operationId) {
      previousOperationIdRef.current = operationId;
      previousStatusRef.current = undefined;
    }
    const data = result.data;
    const status = data?.status;
    const statusChanged = data !== undefined && previousStatusRef.current !== status;
    previousStatusRef.current = status;

    if (statusChanged) {
      onStatusChange?.(data);
    }
  }, [operationId, result.data, onStatusChange]);

  /**
   * Detects errors and calls onError.
   */
  useEffect(() => {
    const callback = onErrorRef.current;
    if (result.isError && result.error != null && callback != null) {
      callback(result.error);
    }
  }, [result.error, result.isError]);

  return result;
}

export function useBulkOperationsQuery(countryCode: CountryCode, enabled: boolean) {
  return useQuery<BulkOperationList, AxiosError>({
    queryKey: [BULK_OPERATIONS_QUERY_KEY_ROOT, countryCode],
    queryFn: () => fetchBulkOperations(countryCode),
    enabled,
  });
}
